import type { LadderState } from "@/lib/types";

export const STATE_LABEL: Record<LadderState, string> = {
  ready: "예정",
  open: "진행중",
  closed: "종료",
};

export const STATE_ORDER: Record<LadderState, number> = {
  open: 0,
  ready: 1,
  closed: 2,
};

// 남은 초를 "HH:MM:SS" 로 표기
export function formatCountdown(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
}

export interface BannerCountdown {
  text: string;
  urgent: boolean;
}

// 홈 배너용 남은 시간 표기
// 1일 이상 → "2일 03:15:22", 1일 미만 → "03:15:22", 1시간 미만 → "15:22"(urgent 강조)
export function formatBannerCountdown(totalSeconds: number): BannerCountdown {
  const s = Math.max(0, Math.floor(totalSeconds));
  const days = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  const hms = `${pad(h)}:${pad(m)}:${pad(sec)}`;
  const urgent = s < 3600;
  if (days >= 1) return { text: `${days}일 ${hms}`, urgent };
  if (urgent) return { text: `${pad(m)}:${pad(sec)}`, urgent };
  return { text: hms, urgent };
}

// "YYYY-MM-DD HH:mm:ss" → "YYYY.MM.DD HH:mm"
export function formatDateTime(value?: string): string {
  if (!value) return "";
  const str = String(value).replace(" ", "T");
  const d = new Date(str);
  if (Number.isNaN(d.getTime())) return value;
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${y}.${mo}.${day} ${hh}:${mm}`;
}

// 각 세로줄(칸)의 x 좌표
export function columnX(slotCnt: number, width: number, margin: number): number[] {
  if (slotCnt <= 1) return [margin];
  const usable = width - margin * 2;
  const xs: number[] = [];
  for (let i = 0; i < slotCnt; i++) {
    xs.push(margin + (i * usable) / (slotCnt - 1));
  }
  return xs;
}

// 출발칸(slot)에서 서버 rungs 를 따라 내려가는 경로의 SVG path d 를 생성.
// 경로는 가로선을 만나면 좌우로 꺾이며, 도착점은 서버가 준 end 와 일치한다.
export function buildPathD(
  slot: number,
  rungs: [number, number][],
  xs: number[],
  topPad: number,
  rowH: number
): string {
  const sorted = [...rungs].sort((a, b) => a[0] - b[0]);
  const maxLevel = sorted.length > 0 ? sorted[sorted.length - 1][0] : 0;
  let col = slot;
  let d = `M ${xs[slot]} ${topPad}`;

  for (let level = 0; level <= maxLevel; level++) {
    const yMid = topPad + level * rowH + rowH / 2;
    const yBot = topPad + (level + 1) * rowH;
    const rung = sorted.find(
      ([l, left]) => l === level && (left === col || left + 1 === col)
    );
    if (rung) {
      const nextCol = rung[1] === col ? col + 1 : col - 1;
      d += ` L ${xs[col]} ${yMid} L ${xs[nextCol]} ${yMid}`;
      col = nextCol;
    }
    d += ` L ${xs[col]} ${yBot}`;
  }

  return d;
}